// ---------------------------------------------------------------------------
// Safe Web Storage helpers
//
// Thin try/catch wrappers around localStorage/sessionStorage. Access throws in
// Safari/Firefox private browsing, when the quota is exceeded, or when storage
// is disabled — these helpers swallow those failures so callers never crash.
// ---------------------------------------------------------------------------

/**
 * Safely JSON.parse a raw storage string, returning `fallback` when the value
 * is absent (`null`) or cannot be parsed.
 */
export function safeJsonParse<T>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Reads a localStorage key, returning null on any failure. */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes a localStorage key, swallowing any failure. */
export function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Throws in incognito/private browsing, quota exceeded, or disabled
  }
}

/** Removes a localStorage key, swallowing any failure. */
export function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

/** Reads a sessionStorage key, returning null on any failure. */
export function safeSessionGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Removes a sessionStorage key, swallowing any failure. */
export function safeSessionRemoveItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore
  }
}
